import React, { useState } from 'react';
import { StarIcon, ChevronDownIcon } from 'lucide-react';

interface Reaction {
  emoji: string;
  fromJid: string;
  fromName: string;
  timestamp: string;
}

interface Message {
  id: string;
  text: string;
  timestamp: string;
  isOutgoing: boolean;
  isImportant?: boolean;
  reactions?: Reaction[];
}
interface MessageBubbleProps {
  message: Message;
  onSwipe: () => void;
  onLongPress: () => void;
  onMenuClick?: () => void; // For desktop - click to open menu
}
export function MessageBubble({
  message,
  onSwipe,
  onLongPress,
  onMenuClick
}: MessageBubbleProps) {
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
    const timer = setTimeout(() => {
      onLongPress();
    }, 500);
    setLongPressTimer(timer);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };
  const handleTouchEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    if (touchStart - touchEnd > 75 && !message.isOutgoing) {
      onSwipe();
    }
    setTouchStart(0);
    setTouchEnd(0);
  };
  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onMenuClick) {
      onMenuClick();
    } else {
      onLongPress();
    }
  };

  return <div
      className={`flex ${message.isOutgoing ? 'justify-end' : 'justify-start'} group`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative flex items-start gap-1">
        {/* Message menu button - appears on hover (desktop) or always visible (mobile) */}
        {!message.isOutgoing && (
          <button
            onClick={handleMenuClick}
            className={`
              flex-shrink-0 p-1.5 rounded-full transition-all
              ${message.isOutgoing ? 'bg-white/20 hover:bg-white/30' : 'bg-gray-100 hover:bg-gray-200'}
              ${isHovered ? 'opacity-100' : 'opacity-0 md:opacity-0'}
              group-hover:opacity-100
              active:scale-95
              mt-1
            `}
            aria-label="Message options"
          >
            <ChevronDownIcon className={`w-4 h-4 ${message.isOutgoing ? 'text-white' : 'text-gray-600'}`} />
          </button>
        )}

        <div className={`max-w-[75%] rounded-2xl px-4 py-3 shadow-sm relative ${message.isOutgoing ? 'bg-[#5B8E87] text-white rounded-tr-sm' : 'bg-white text-[#212121] rounded-tl-sm'}`}>
          {message.isImportant && <StarIcon className={`w-4 h-4 absolute -top-2 -right-2 fill-current ${message.isOutgoing ? 'text-[#C9A870]' : 'text-[#C9A870]'}`} />}
          <p className="text-sm leading-relaxed break-words whitespace-pre-wrap">{message.text}</p>

          {/* Reaction Display */}
          {message.reactions && message.reactions.length > 0 && (
            <div className="flex gap-1 mt-2 flex-wrap">
              {(() => {
                // Aggregate reactions by emoji
                const reactionCounts = message.reactions.reduce((acc, reaction) => {
                  const emoji = reaction.emoji;
                  if (!acc[emoji]) {
                    acc[emoji] = {
                      count: 0,
                      users: []
                    };
                  }
                  acc[emoji].count++;
                  acc[emoji].users.push(reaction.fromName);
                  return acc;
                }, {} as Record<string, { count: number; users: string[] }>);

                return Object.entries(reactionCounts).map(([emoji, data]) => (
                  <span
                    key={emoji}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      message.isOutgoing
                        ? 'bg-white/20 text-white'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                    title={data.users.join(', ')}
                  >
                    <span>{emoji}</span>
                    {data.count > 1 && <span className="text-xs">{data.count}</span>}
                  </span>
                ));
              })()}
            </div>
          )}

          <div className="flex items-center justify-end gap-1 mt-1">
            <span className={`text-xs ${message.isOutgoing ? 'text-white text-opacity-70' : 'text-gray-500'}`}>
              {message.timestamp}
            </span>
          </div>
        </div>

        {/* Message menu button for outgoing messages - on the right */}
        {message.isOutgoing && (
          <button
            onClick={handleMenuClick}
            className={`
              flex-shrink-0 p-1.5 rounded-full transition-all
              ${message.isOutgoing ? 'bg-white/20 hover:bg-white/30' : 'bg-gray-100 hover:bg-gray-200'}
              ${isHovered ? 'opacity-100' : 'opacity-0 md:opacity-0'}
              group-hover:opacity-100
              active:scale-95
              mt-1
            `}
            aria-label="Message options"
          >
            <ChevronDownIcon className={`w-4 h-4 ${message.isOutgoing ? 'text-white' : 'text-gray-600'}`} />
          </button>
        )}
      </div>
    </div>;
}